import { takeLatest, call, put } from 'redux-saga/effects';
import { callWithAuth } from '../sagaApiHelper';
import { initiatePaymentApi, listPlansApi, subscriptionApi, subscriptionStatusApi } from '../../api/apiLists/billingApi';
import { getPlansFromStorage, savePlansToStorage } from '../../utils/apiCacheHelper';
import { showAlert } from '../alertSlice';
import {
  initiatePaymentRequest, initiatePaymentSuccess, initiatePaymentFailure,
  listPlansRequest, listPlansSuccess, listPlansFailure,
  getBillingDetailsRequest, getBillingDetailsSuccess, getBillingDetailsFailure,
  createSubscriptionRequest, createSubscriptionSuccess, createSubscriptionFailure,
  saveBillingDetailsRequest, saveBillingDetailsSuccess, saveBillingDetailsFailure,
  getSubscriptionStatusRequest, getSubscriptionStatusSuccess, getSubscriptionStatusFailure,
  cancelPaymentRequest, cancelPaymentSuccess, cancelPaymentFailure,
} from '../billingSlice';

function* handleApiFailure(failureAction, errorOrMessage, fallbackMessage) {
  const message = typeof errorOrMessage === 'string'
    ? errorOrMessage
    : errorOrMessage?.message || fallbackMessage;
  yield put(failureAction(message));
  yield put(showAlert({ message, type: 'error' }));
}

export function* handleInitiatePayment(action) {
  try {
    const { paymentFlow, ...apiBody } = action.payload || {};
    yield put(initiatePaymentRequest({ paymentFlow }));
    const res = yield call(callWithAuth, initiatePaymentApi, apiBody);
    if (res?.status === true || res?.paymentId) {
      yield put(initiatePaymentSuccess(res));
    } else {
      yield* handleApiFailure(initiatePaymentFailure, res?.msg, 'An unexpected error occurred while initiating payment.');
    }
  } catch (error) {
    yield* handleApiFailure(initiatePaymentFailure, error, 'An unexpected error occurred while initiating payment.');
  }
}

export function* handleListPlans() {
  try {
    const cachedPlans = getPlansFromStorage();
    if (cachedPlans) {
      yield put(listPlansSuccess(cachedPlans));
      return;
    }

    yield put(listPlansRequest());
    const res = yield call(callWithAuth, listPlansApi, { operation: 'listPlans', body: {} });
    if (res?.plans && Array.isArray(res.plans)) {
      const dataToSave = { plans: res.plans, billingTabs: res.billingTabs || [] };
      savePlansToStorage(dataToSave);
      yield put(listPlansSuccess(dataToSave));
    } else {
      yield* handleApiFailure(listPlansFailure, res?.msg, 'Failed to load plans.');
    }
  } catch (error) {
    yield* handleApiFailure(listPlansFailure, error, 'Failed to load plans.');
  }
}

export function* handleGetBillingDetails() {
  try {
    yield put(getBillingDetailsRequest());
    const res = yield call(callWithAuth, subscriptionApi, { operation: 'getBillingDetails', body: {} });
    if (res?.billingDetails) {
      yield put(getBillingDetailsSuccess(res.billingDetails));
    } else {
      yield put(getBillingDetailsFailure(res?.msg || 'Failed to load billing details.'));
    }
  } catch (error) {
    yield put(getBillingDetailsFailure(error?.message || 'Failed to load billing details.'));
  }
}

export function* handleCreateSubscription(action) {
  try {
    yield put(createSubscriptionRequest());
    const res = yield call(callWithAuth, subscriptionApi, {
      operation: 'createSubscription',
      body: { planId: action.payload.planId },
    });
    if (res?.subscription) {
      yield put(createSubscriptionSuccess(res.subscription));
    } else {
      yield* handleApiFailure(createSubscriptionFailure, res?.msg, 'Failed to create subscription.');
    }
  } catch (error) {
    yield* handleApiFailure(createSubscriptionFailure, error, 'Failed to create subscription.');
  }
}

export function* handleSaveBillingDetails(action) {
  try {
    yield put(saveBillingDetailsRequest());
    const res = yield call(callWithAuth, subscriptionApi, {
      operation: 'saveBillingDetails',
      body: action.payload,
    });
    if (res?.billingDetails || res?.success || !res?.msg) {
      yield put(saveBillingDetailsSuccess(res?.billingDetails ?? null));
    } else {
      yield* handleApiFailure(saveBillingDetailsFailure, res?.msg, 'Failed to save billing details.');
    }
  } catch (error) {
    yield* handleApiFailure(saveBillingDetailsFailure, error, 'Failed to save billing details.');
  }
}

export function* handleGetSubscriptionStatus(action) {
  try {
    yield put(getSubscriptionStatusRequest());
    const res = yield call(callWithAuth, subscriptionStatusApi, {
      operation: 'getSubscriptionStatus',
      payload: { subscriptionId: action.payload.subscriptionId },
    });
    if (res?.subscriptionId || res?.status) {
      yield put(getSubscriptionStatusSuccess(res));
    } else {
      yield put(getSubscriptionStatusFailure(res?.msg || 'Failed to get subscription status.'));
    }
  } catch (error) {
    yield put(getSubscriptionStatusFailure(error?.message || 'Failed to get subscription status.'));
  }
}

export function* handleCancelPayment(action) {
  try {
    yield put(cancelPaymentRequest());
    const res = yield call(callWithAuth, subscriptionApi, {
      operation: 'cancelPayment',
      body: { subscriptionId: action.payload.subscriptionId },
    });
    if (res?.status === 'PAYMENT_CANCELLED' || res?.message?.toLowerCase()?.includes('cancelled')) {
      yield put(cancelPaymentSuccess(res));
      yield put(showAlert({ message: res.message || 'Payment cancelled successfully.', type: 'success' }));
    } else {
      yield* handleApiFailure(cancelPaymentFailure, res?.msg || 'Failed to cancel payment.', 'Failed to cancel payment.');
    }
  } catch (error) {
    yield* handleApiFailure(cancelPaymentFailure, error, 'Failed to cancel payment.');
  }
}

export default function* billingSaga() {
  yield takeLatest('INITIATE_PAYMENT', handleInitiatePayment);
  yield takeLatest('LIST_PLANS', handleListPlans);
  yield takeLatest('GET_BILLING_DETAILS', handleGetBillingDetails);
  yield takeLatest('CREATE_SUBSCRIPTION', handleCreateSubscription);
  yield takeLatest('SAVE_BILLING_DETAILS', handleSaveBillingDetails);
  yield takeLatest('GET_SUBSCRIPTION_STATUS', handleGetSubscriptionStatus);
  yield takeLatest('CANCEL_PAYMENT', handleCancelPayment);
}